import React, { useMemo } from 'react';
import { useTeam } from "../../providers/TeamProvider";
import CircleProgress from "../../components/CircleProgress";
import User from "../../models/User";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Img } from "react-image";
import LoadingSpinner from "../../components/LoadingSpinner";
import UserImageCircle from "../../components/UserImageCircle";
import InfoTooltip from "../../components/InfoTooltip";
import TeamMatchDetails from "./TeamMatchDetails";
import TalentDisplayName from '../../components/TalentDisplayName';


const TeamHorizontalTile = (props) => {
    const { team, canFavorite, criteria, removeFromFavorites, addToFavorites } = useTeam();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const vips = TB.filter(team.topPositionsForDisplay(), vip => TB.isDefined(vip)).map(vip => User.make({ ...vip }));


    const teamMemberLink = useMemo(() => {
        const params = { team_id: team.id }

        const state = {
            linkParams: {
                title: 'Back',
                path: location.pathname + (location.search || '')
            }
        }
        return { state, params }
    }, [team, location.pathname]);


    const showMatchDetails = () => {
        let matchCriteria = {};
        if (criteria) {
            matchCriteria = criteria;
        }

        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            content: (
                <TeamMatchDetails
                    team={team}
                    isEngagementLead={false}
                    criteria={matchCriteria}
                />
            )
        })
    }

    const tooltipId = `${team?.id}-browse`
    const favoriteId = "fav-btn-" + tooltipId;

    return (
        <div className="team-horizontal-tile p-4 pb-0 mb-8 bg-wrippleOffWhite">

            <div className="bg-white w-full">
                <div className="p-4 md:flex justify-between md:space-x-12 border-b border-[#CECECE]">

                    <div className="w-full md:w-1/3 flex justify-between md:pb-0 pb-4">
                        <div>
                            <p className="text-3xl font-bold">{team.name}</p>
                            {(team.type === 'Agency' && team.supplier_client) ? (
                                <Link className="text-primary flex items-center gap-2 text-xs md:text-sm hover:underline" to={`/agency-profiles/${team.supplier_client.id}`}>
                                    {team.type}
                                    <img className="h-7" src={team.supplier_client.logo_path} />
                                </Link>
                            ) : (
                                <p>{team.type}</p>
                            )}
                        </div>

                        <div className="md:hidden">
                            {canFavorite && (
                                <div className="flex items-start">
                                    <button
                                        className="btn like-btn pt-0"
                                        onClick={() => props.onRequest(
                                            team.is_favorite ? removeFromFavorites : addToFavorites
                                        )}
                                        id={favoriteId}
                                        disabled={props.isDisabled}
                                    >
                                        <i className={"text-danger fa-heart text-2xl " + (team.is_favorite ? "fas" : "far")} />
                                    </button>

                                    <InfoTooltip key={favoriteId + 'tooltip'} type={"dark"} target={favoriteId}>
                                        {team.is_favorite ? "Remove from saved" : "Save as Favorite"}
                                    </InfoTooltip>
                                </div>
                            )}
                        </div>
                    </div>



                    <div className="w-full text-centr md:w-2/3 md:flex">
                        <div className="w-full">

                            <div className="line-clamp-2 mb-2">
                                <div className="text-sm" dangerouslySetInnerHTML={{ __html: team.fullBio() }}>

                                </div>
                            </div>

                            <Link className="details-link text-base" to={{ ...location, pathname: `team-profiles/${team.id}` }}>
                                {TB.trans('teams.project_info_panel.detail_link')}
                            </Link>
                        </div>

                        <div className="hidden md:block">
                            {canFavorite && (
                                <div className="flex items-start">
                                    <button
                                        className="btn like-btn pt-0"
                                        onClick={() => props.onRequest(
                                            team.is_favorite ? removeFromFavorites : addToFavorites
                                        )}
                                        id={favoriteId}
                                        disabled={props.isDisabled}
                                    >
                                        <i className={"text-danger fa-heart text-2xl " + (team.is_favorite ? "fas" : "far")} />
                                    </button>

                                    <InfoTooltip key={favoriteId + 'tooltip'} type={"dark"} target={favoriteId}>
                                        {team.is_favorite ? "Remove from saved" : "Save as Favorite"}
                                    </InfoTooltip>
                                </div>
                            )}
                        </div>

                    </div>

                </div>

                <div className="p-4 md:flex justify-between md:space-x-12">

                    <div className="w-full md:w-1/3 md:border-r pb-3 border-[#CECECE] flex md:flex-col justify-center gap-4">
                        <div className="h-full flex justify-center">
                            <CircleProgress
                                className="metric-circle self-center w-40"
                                progress={team.overallMatchPercent()}
                                strokeWidth={7}
                            >
                                <p>
                                    <span className="text-2xl">{team.overallMatchPercent(true)}%</span>
                                    <small className="text-base">Match</small>
                                </p>
                            </CircleProgress>
                        </div>

                        <button type="button" className="details-link" onClick={showMatchDetails}>More</button>
                    </div>

                    <div className="w-full md:w-2/3 pt-6 md:pt-0">
                        <div className="flex gap-3 justify-btween items-center">
                            <p className="text-xl text-primary">Team Members</p>
                            <Link className="details-link text-xs md:text-sm" to={{ ...location, pathname: `team-profiles/${team.id}` }}>
                                View all {team.positions?.length} members
                            </Link>
                        </div>


                        {/* FOR MOBILE */}
                        <div className="pt-4 flex flex-col md:hidden divide-y">
                            {vips.map((user, i) => {
                                const filteredDisciplines = user.disciplines.filter(d => !!d.name);
                                return (
                                    <div key={`${user.id}-${team.id}`} className="flex flex-1 gap-6 py-3 items-center">

                                        <div className="flex-shrink-0">

                                            <Link to={{
                                                ...location,
                                                pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                                state: teamMemberLink.state
                                            }} className="flex-shrink-0">
                                                <Img
                                                    loader={(
                                                        <div className="justify-center h-20 w-20 items-center flex flex-col">
                                                            <LoadingSpinner />
                                                        </div>
                                                    )}
                                                    unloader={<UserImageCircle user={user} hideTooltip={true} size={60} />}
                                                    src={user.image_path}
                                                    className="img-grayscale img-fluid h-20 w-20 rounded-full " />
                                            </Link>

                                            <Link className="details-link block text-center pt-2 text-xs md:text-sm" to={{
                                                ...location,
                                                pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                                state: teamMemberLink.state
                                            }}>
                                                View Profile
                                            </Link>
                                        </div>

                                        <div className="flex">
                                            <TalentDisplayName talent={user}>
                                                <p className="member-name">{user.firstNamePlusInitial()}</p>
                                            </TalentDisplayName>

                                            <p className="line-clamp-1 w-full md:w-3/4 text-xxs md:text-center">
                                                {user.job_title}
                                            </p>

                                            <div className="py-2 matched-roles-teams">
                                                <div className="flex gap-2 items-center flex-wrap">
                                                    {filteredDisciplines.slice(0, 2)?.map(item => {
                                                        return (
                                                            <p className={TB.mapClass("role2 text-center", {
                                                                'max-w-[260px]': filteredDisciplines?.length === 1,
                                                                'max-w-[150px]': filteredDisciplines?.length === 2
                                                            })} key={`r-${item.id}`}>
                                                                {item.name}
                                                            </p>
                                                        )
                                                    })}
                                                </div>
                                            </div>

                                            {filteredDisciplines?.length > 2 && (
                                                <div>
                                                    <p id={`matched-roles-${team.id}-${user.id}`} className="more-roles text-xs md:text-xxs">
                                                        + {TB.pluralize('role', filteredDisciplines?.length - 2, true)}
                                                    </p>

                                                    <InfoTooltip
                                                        type="dark"
                                                        placement="top"
                                                        target={`matched-roles-${team.id}-${user.id}`}
                                                    >
                                                        {filteredDisciplines.slice(2).map(discipline => (
                                                            <p key={discipline.id} className="px-3 py-1 m-0 text-white text-left">
                                                                {discipline.name}
                                                            </p>
                                                        ))}
                                                    </InfoTooltip>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {/* FOR DESKTOP */}
                        <div className="hidden md:flex pt-4 justify-between gap-6">
                            {vips.map((user, i) => {
                                const filteredDisciplines = user.disciplines.filter(d => !!d.name);
                                return (
                                    <div key={`${user.id}-${team.id}`} className="flex flex-col items-center bg-wrippleOffWhite w-full px-2 py-4">
                                        <Link to={{
                                            ...location,
                                            pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                            state: teamMemberLink.state
                                        }}>
                                            {/* <Img
                                                loader={(
                                                    <div className="justify-center h-20 w-20 items-center flex flex-col">
                                                        <LoadingSpinner />
                                                    </div>
                                                )}
                                                unloader={<UserImageCircle user={user} hideTooltip={true} size={60} />}
                                                src={user.image_path}
                                                className="img-grayscale img-fluid h-20 w-20 rounded-full" /> */}
                                            <UserImageCircle user={user} hideTooltip={true} size={80} className="img-grayscale img-fluid h-20 w-20 rounded-full" />
                                        </Link>

                                        <TalentDisplayName talent={user}>
                                            <p className="member-name pt-1.5">{user.firstNamePlusInitial()}</p>
                                        </TalentDisplayName>

                                        <p className="line-clamp-1 w-3/4 text-xxs text-center">
                                            {user.job_title}
                                        </p>


                                        <div className="p-2 matched-roles-teams">
                                            <div className="space-y-2">
                                                {filteredDisciplines.slice(0, 2)?.map(item => {
                                                    return (
                                                        <p className={TB.mapClass("role2 text-center", {
                                                            'max-w-[260px]': filteredDisciplines?.length === 1,
                                                            'max-w-[150px]': filteredDisciplines?.length === 2
                                                        })} key={`r-${item.id}`}>
                                                            {item.name}
                                                        </p>
                                                    )
                                                })}
                                            </div>
                                        </div>

                                        {filteredDisciplines?.length > 2 && (
                                            <div>
                                                <p id={`matched-roles-${team.id}-${user.id}`} className="more-roles">
                                                    + {TB.pluralize('role', filteredDisciplines?.length - 2, true)}
                                                </p>

                                                <InfoTooltip
                                                    type="dark"
                                                    placement="top"
                                                    target={`matched-roles-${team.id}-${user.id}`}
                                                >
                                                    {filteredDisciplines.slice(2).map(discipline => (
                                                        <p key={discipline.id} className="px-3 py-1 m-0 text-white text-left">
                                                            {discipline.name}
                                                        </p>
                                                    ))}
                                                </InfoTooltip>
                                            </div>
                                        )}

                                        <Link className="details-link text-sm pt-2 mt-auto" to={{
                                            ...location,
                                            pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                            state: teamMemberLink.state
                                        }}>
                                            View Profile
                                        </Link>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>

                <div className="bg-wrippleOffWhite p-4 md:flex justify-end md:space-x-12">
                    <div className={TB.mapClass('candidate-panel w-auto no-min-width bg-transparent m-0 md:pr-12', {
                        'md:border-r border-[#CECECE]': !props.isBrowse,
                    })}>
                        <div className="candidate-rate-section p-2 bg-transparent">
                            <h4>Average Standard Rate</h4>
                            <h2 className="currency-sup">
                                {team.rangedPlatformRates()}
                                <span>/hr</span>
                            </h2>
                        </div>
                    </div>

                    {!props.isBrowse && (
                        <div className="flex items-center justify-center pt-4 md:pt-0 md:w-[30%]">
                            {props.children || (
                                <button className="btn btn-primary btn-block request-tn text-base min-w-[250px] w-full">
                                    Invite to Project
                                </button>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

export default TeamHorizontalTile;